import React from 'react';
import AttendancePage from './pages/Attendance';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './config/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import MainLayout from './components/Layout/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Birds from './pages/Birds';
import BirdDetail from './pages/BirdDetail';
import BirdLogs from './pages/BirdLogs';
import BirdProcurementAssignees from './pages/BirdProcurementAssignees';
import Procurements from './pages/Procurements';
import ProcurementDetail from './pages/ProcurementDetail';
import Mortalities from './pages/Mortalities';
import EggProduction from './pages/EggProduction';
import BirdEggProduction from './pages/BirdEggProduction';
import EggCorrelation from './pages/EggCorrelation';
import EggSales from './pages/EggSales';
import AllExpendituresPage from './pages/AllExpenditures';
import FeedManagement from './pages/FeedManagement';
import Customers from './pages/Customers';
import CustomerSmsLogs from './pages/CustomerSmsLogs';
import FeedStockLevels from './pages/FeedStockLevels';
import FeedProcurements from './pages/FeedProcurements';
import FeedBatchAssignments from './pages/FeedBatchAssignments';
import FeedCalculator from './pages/FeedCalculator';
import RawMaterials from './pages/RawMaterials';
import FeedProduction from './pages/FeedProduction';
import FeedProductionTemplates from './pages/FeedProductionTemplates';
import ProcurementPlanner from './pages/ProcurementPlanner';
import FeedTransitionTracker from './pages/FeedTransitionTracker';
import FeedSwitchCampaign from './pages/FeedSwitchCampaign';
import FeedConversionRatio from './pages/FeedConversionRatio';
import ProductionCurve from './pages/ProductionCurve';
import CashFlowProjection from './pages/CashFlowProjection';
import FinancialSummary from './pages/FinancialSummary';
import BirdWeights from './pages/BirdWeights';
import FinancialCycles from './pages/FinancialCycles';
import FinancialCycleDetail from './pages/FinancialCycleDetail';
import CyclePerformance from './pages/CyclePerformance';
import UserPermissions from './pages/UserPermissions';
import Requests from './pages/Requests';
import VetInstructions from './pages/VetInstructions';
import Prices from './pages/Prices';
import PricingSuggestions from './pages/PricingSuggestions';
import Employees from './pages/Employees';
import BatchEmployeeAssignments from './pages/BatchEmployeeAssignments';
import VaccinationAlerts from './pages/VaccinationAlerts';
import VaccinationCalendar from './pages/VaccinationCalendar';
import VetVisitReports from './pages/VetVisitReports';
import CapitalInvestments from './pages/CapitalInvestments';
import CapitalInvestmentDetail from './pages/CapitalInvestmentDetail';
import SuppliersAndProcurements from './pages/SuppliersAndProcurements';
import Medications from './pages/Medications';
import CrateStocks from './pages/CrateStocks';
import PreventiveCalendar from './pages/preventive-health/PreventiveCalendar';
import ProgrammeEditor from './pages/preventive-health/ProgrammeEditor';
import AlertsDashboard from './pages/preventive-health/AlertsDashboard';
import PHReports from './pages/preventive-health/PHReports';
import AnalyticsExport from './pages/AnalyticsExport';
import EggProductionComparison from './pages/EggProductionComparison';
import RawMaterialPriceImpact from './pages/RawMaterialPriceImpact';
import FlockWeightTrend from './pages/FlockWeightTrend';
import WeightSubmissionsInbox from './pages/WeightSubmissionsInbox';

// Create theme with dark mode support
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ff9800',
      light: '#ffb333',
      dark: '#c77700',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ffa726',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    background: {
      default: '#0a0a0a',
      paper: '#141414',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    divider: 'rgba(255, 152, 0, 0.15)',
    action: {
      active: '#ff9800',
      hover: 'rgba(255, 152, 0, 0.12)',
      selected: 'rgba(255, 152, 0, 0.2)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.01em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.04)',
    '0px 4px 8px rgba(0,0,0,0.06)',
    '0px 6px 12px rgba(0,0,0,0.08)',
    '0px 8px 16px rgba(0,0,0,0.1)',
    '0px 12px 24px rgba(0,0,0,0.12)',
    '0px 16px 32px rgba(0,0,0,0.14)',
    '0px 20px 40px rgba(0,0,0,0.16)',
    '0px 24px 48px rgba(0,0,0,0.18)',
    '0px 2px 4px rgba(0,0,0,0.04)',
    '0px 4px 8px rgba(0,0,0,0.06)',
    '0px 6px 12px rgba(0,0,0,0.08)',
    '0px 8px 16px rgba(0,0,0,0.1)',
    '0px 12px 24px rgba(0,0,0,0.12)',
    '0px 16px 32px rgba(0,0,0,0.14)',
    '0px 20px 40px rgba(0,0,0,0.16)',
    '0px 24px 48px rgba(0,0,0,0.18)',
    '0px 2px 4px rgba(0,0,0,0.04)',
    '0px 4px 8px rgba(0,0,0,0.06)',
    '0px 6px 12px rgba(0,0,0,0.08)',
    '0px 8px 16px rgba(0,0,0,0.1)',
    '0px 12px 24px rgba(0,0,0,0.12)',
    '0px 16px 32px rgba(0,0,0,0.14)',
    '0px 20px 40px rgba(0,0,0,0.16)',
    '0px 24px 48px rgba(0,0,0,0.18)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 24px',
          fontSize: '0.95rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(131, 65, 5, 0.2)',
            transform: 'translateY(-1px)',
            transition: 'all 0.2s ease-in-out',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: '#000000',
          '&:hover': {
            background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#141414',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.6)',
          transition: 'all 0.2s ease-in-out',
          border: '1px solid rgba(255, 152, 0, 0.1)',
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(0,0,0,0.6)',
        },
        elevation2: {
          boxShadow: '0px 4px 12px rgba(0,0,0,0.7)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#141414',
          border: '1px solid rgba(255, 152, 0, 0.2)',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.6)',
          '&:hover': {
            boxShadow: '0px 6px 16px rgba(255, 152, 0, 0.3)',
            borderColor: 'rgba(255, 152, 0, 0.4)',
            transform: 'translateY(-2px)',
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&:hover fieldset': {
              borderColor: '#ff9800',
            },
            '& fieldset': {
              borderColor: 'rgba(255,255,255,0.23)',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0px 12px 40px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'rgba(255, 152, 0, 0.15)',
            color: '#ff9800',
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0f0f0f',
          borderRight: '1px solid rgba(255, 152, 0, 0.15)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0f0f0f',
          borderBottom: '1px solid rgba(255, 152, 0, 0.15)',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          minWidth: 40,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 152, 0, 0.12)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 152, 0, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 152, 0, 0.25)',
            },
          },
        },
      },
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <MainLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="birds" element={<Birds />} />
                <Route path="birds/:id" element={<BirdDetail />} />
                <Route path="weight-trend" element={<FlockWeightTrend />} />
                <Route path="weight-trend/:id" element={<FlockWeightTrend />} />
                <Route path="birds/:id/weight-trend" element={<FlockWeightTrend />} />
                <Route path="weight-submissions" element={<WeightSubmissionsInbox />} />
                <Route path="eggs/production" element={<EggProduction />} />
                <Route path="eggs/production/bird" element={<BirdEggProduction />} />
                <Route path="eggs/correlation" element={<EggCorrelation />} />
                <Route path="eggs/production-curve" element={<ProductionCurve />} />
                <Route path="eggs/comparison" element={<EggProductionComparison />} />
                <Route path="financial/sales" element={<EggSales />} />
                <Route path="customers" element={<Customers />} />
                <Route path="customers/sms-logs" element={<CustomerSmsLogs />} />
                <Route path="financial/cycles" element={<FinancialCycles />} />
                <Route path="financial/cycles/:id" element={<FinancialCycleDetail />} />
                <Route path="financial/cycle-performance" element={<CyclePerformance />} />
                <Route path="financial/expenditures" element={<AllExpendituresPage />} />
                <Route path="feed" element={<FeedManagement />} />
                <Route path="feed/stock" element={<FeedStockLevels />} />
                <Route path="feed/procurements" element={<FeedProcurements />} />
                <Route path="feed/assignments" element={<FeedBatchAssignments />} />
                <Route path="feed/calculator" element={<FeedCalculator />} />
                <Route path="feed/raw-materials" element={<RawMaterials />} />
                <Route path="feed/production" element={<FeedProduction />} />
                <Route path="feed/templates" element={<FeedProductionTemplates />} />
                <Route path="feed/procurement-planner" element={<ProcurementPlanner />} />
                <Route path="feed/transition-tracker" element={<FeedTransitionTracker />} />
                <Route path="feed/switch" element={<FeedSwitchCampaign />} />
                <Route path="feed/fcr" element={<FeedConversionRatio />} />
                <Route path="financial/cash-flow" element={<CashFlowProjection />} />
                <Route path="financial/summary" element={<FinancialSummary />} />
                <Route path="bird-weights" element={<BirdWeights />} />
                <Route path="bird-logs" element={<BirdLogs />} />
                <Route path="bird-procurement-assignees" element={<BirdProcurementAssignees />} />
                <Route path="procurements" element={<Procurements />} />
                <Route path="procurements/:id" element={<ProcurementDetail />} />
                <Route path="mortalities" element={<Mortalities />} />
                <Route path="requests" element={<Requests />} />
                <Route path="vet-instructions" element={<VetInstructions />} />
                <Route path="vet-visit-reports" element={<VetVisitReports />} />
                <Route path="security/permissions" element={<UserPermissions />} />
                <Route path="prices" element={<Prices />} />
                <Route path="pricing-suggestions" element={<PricingSuggestions />} />
                <Route path="employees" element={<Employees />} />
                <Route path="batch-employee-assignments" element={<BatchEmployeeAssignments />} />
                <Route path="vaccination-alerts" element={<VaccinationAlerts />} />
                <Route path="vaccination-schedule" element={<VaccinationCalendar />} />
                <Route path="capital-investments" element={<CapitalInvestments />} />
                <Route path="capital-investments/:id" element={<CapitalInvestmentDetail />} />
                <Route path="financial/suppliers" element={<SuppliersAndProcurements />} />
                <Route path="medications" element={<Medications />} />
                <Route
                  path="medications/transactions"
                  element={<Navigate to="/medications" replace />}
                />
                <Route path="crate-stocks" element={<CrateStocks />} />
                <Route path="preventive-health/calendar" element={<PreventiveCalendar />} />
                <Route path="preventive-health/programmes" element={<ProgrammeEditor />} />
                <Route path="preventive-health/alerts" element={<AlertsDashboard />} />
                <Route path="preventive-health/reports" element={<PHReports />} />
                <Route path="analytics/export" element={<AnalyticsExport />} />
                <Route path="attendance" element={<AttendancePage />} />
                <Route path="feed/price-impact" element={<RawMaterialPriceImpact />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
